/**
 * Demo API hooks
 * Custom hooks for demo signup, verification, status polling, and magic link.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { DemoProfileStatus } from '@hapana/shared/schemas/demo/demo-profile-schemas'
import { auth } from '../../config/firebase.config'

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL || ''
const API_BASE = `${GATEWAY_URL}/api/v1/demo`

/**
 * Generic fetch wrapper.
 * Automatically attaches the Firebase auth token when available.
 */
async function demoFetch(path, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...options.headers }

  // Attach auth token if the user is signed in
  try {
    const currentUser = auth.currentUser
    if (currentUser) {
      const token = await currentUser.getIdToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }
  } catch (_) {
    // Non-critical — proceed without auth header
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })
  const data = await response.json()
  if (!response.ok) {
    throw new Error(data.error || 'Request failed')
  }
  return data
}

/**
 * Hook for demo signup
 */
export function useDemoSignup() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const signup = useCallback(async (input) => {
    setLoading(true)
    setError(null)
    try {
      const result = await demoFetch('/signup', {
        method: 'POST',
        body: JSON.stringify(input),
      })
      setData(result)
      return result
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return { signup, loading, error, data }
}

/**
 * Hook for demo verification
 */
export function useDemoVerify() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const verify = useCallback(async (token, demoProfileId) => {
    setLoading(true)
    setError(null)
    try {
      const result = await demoFetch('/verify', {
        method: 'POST',
        body: JSON.stringify({ token, demoProfileId }),
      })
      setData(result)
      return result
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return { verify, loading, error, data }
}

/**
 * Hook for polling demo status
 */
export function useDemoStatus(demoProfileId, { enabled = true, interval = 3000 } = {}) {
  const [status, setStatus] = useState(null)
  const [error, setError] = useState(null)
  const intervalRef = useRef(null)

  useEffect(() => {
    if (!enabled || !demoProfileId) {
      return
    }

    const poll = async () => {
      try {
        const result = await demoFetch(`/status/${demoProfileId}`)
        setStatus(result)

        // Stop polling when ready or failed
        if (
          result.status === DemoProfileStatus.READY ||
          result.status === DemoProfileStatus.FAILED ||
          result.status === DemoProfileStatus.EXPIRED
        ) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
        }
      } catch (err) {
        setError(err.message)
      }
    }

    // Immediate first poll
    poll()
    intervalRef.current = setInterval(poll, interval)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [demoProfileId, enabled, interval])

  return { status, error }
}

/**
 * Hook for sending magic link
 */
export function useDemoMagicLink() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [sent, setSent] = useState(false)

  const sendMagicLink = useCallback(async (email) => {
    setLoading(true)
    setError(null)
    try {
      await demoFetch('/send-magic-link', {
        method: 'POST',
        body: JSON.stringify({ email }),
      })
      setSent(true)
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return { sendMagicLink, loading, error, sent }
}

/**
 * Hook for sending magic link to trial users.
 * Calls /api/v1/auth/send-magic-link (different from demo endpoint).
 */
export function useTrialMagicLink() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [sent, setSent] = useState(false)

  const sendMagicLink = useCallback(async (email) => {
    setLoading(true)
    setError(null)
    try {
      const gatewayUrl = import.meta.env.VITE_GATEWAY_URL || ''
      // Extract subdomain from browser hostname since gateway receives requests at localhost
      const hostname = window.location.hostname
      const parts = hostname.split('.')
      const urlPrefix = parts.length > 1 ? parts[0] : null
      const response = await fetch(`${gatewayUrl}/api/v1/auth/send-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ...(urlPrefix && { urlPrefix }) }),
      })
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || 'Request failed')
      }
      // Persist the email so the /magic-link callback can complete sign-in.
      // signInWithEmailLink needs the original recipient email, and the redirect
      // URL doesn't carry it. Store the server-normalised form (lowercase+trim)
      // so it matches the address Firebase generated the link for. Mirrors the
      // demo flow's `demoEmailForSignIn`; the callback reads `trialEmailForSignIn`.
      localStorage.setItem('trialEmailForSignIn', email.trim().toLowerCase())
      setSent(true)
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return { sendMagicLink, loading, error, sent }
}

/**
 * Hook for website lookup with debounce
 */
export function useWebsiteLookup(debounceMs = 1000) {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const timerRef = useRef(null)
  const abortRef = useRef(null)

  const lookup = useCallback(
    (url) => {
      // Clear previous timer and abort controller
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      if (abortRef.current) {
        abortRef.current.abort()
      }

      // Reset if URL is empty or too short
      if (!url || url.length < 4) {
        setResult(null)
        setLoading(false)
        return
      }

      // Don't show loading immediately — wait for debounce
      setResult(null)

      timerRef.current = setTimeout(async () => {
        const controller = new AbortController()
        abortRef.current = controller
        setLoading(true)

        try {
          let normalizedUrl = url.trim()
          if (!normalizedUrl.startsWith('http')) {
            normalizedUrl = `https://${normalizedUrl}`
          }

          const response = await fetch(`${API_BASE}/lookup-website`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url: normalizedUrl }),
            signal: controller.signal,
          })
          const data = await response.json()
          if (!controller.signal.aborted) {
            setResult(data)
          }
        } catch (err) {
          if (err.name !== 'AbortError') {
            setResult({ found: false })
          }
        } finally {
          if (!controller.signal.aborted) {
            setLoading(false)
          }
        }
      }, debounceMs)
    },
    [debounceMs]
  )

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      if (abortRef.current) {
        abortRef.current.abort()
      }
    }
  }, [])

  return { lookup, loading, result }
}

/**
 * Hook for resending verification
 */
export function useDemoResendVerification() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const resend = useCallback(async (demoProfileId) => {
    setLoading(true)
    setError(null)
    try {
      await demoFetch('/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ demoProfileId }),
      })
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return { resend, loading, error }
}
